import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import ClickableContainer from '../../h_cctv/RealtimeCCTV/ClickableContainer';
import TableLamp from '../../../components/TableLamp';
import { BtnBlue, BtnRed } from '../../../components/Button';
import { InputTable } from '../../../assets/styles/InputTable';
import { ButtonsWrapper } from '../../sm_setting/setting7/cctvInfoStyle';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSetAuth } from '../../../utils/useSetAuth';
import { useRecoilValue } from 'recoil';
import { userState } from '../../../atoms';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost } from '../../../services/_common';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import { v4 } from 'uuid';
import Portal from '../../../components/Portal';
import DeleteModal from '../../../components/Modal/DeleteModal';
import { IModal } from 'customTypes';
import { trimArray } from '../../../utils/trimArray';
import { applyBorderStyle } from '../../../utils/applyBorderStyle';
import { characterRegex } from '../../../utils/checkRegex';
import { logPost } from '../../../services/log';

const Root = styled.div``;

interface IId {
  id: string;
}
interface ICar extends IId {
  ccCd: string;
  ccName: string;
  ccNum: string;
  wDate: string;
  writer: string;
  eDate: string;
  editor: string;
  useYn: 'Y' | 'N';
  flag: boolean;
}

const Vehicle = () => {
  const { t } = useTranslation();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const size = useOutletContext<any>();
  const userInfo = useRecoilValue(userState);
  const [tableState, setTableState] = useState<ICar[]>([]);
  const [isSaveClicked, setIsSaveClicked] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<IModal>({ status: false, type: '', title: '' });
  const scrollContainerRef = useRef<HTMLInputElement>(null);
  // const [carData, setCarData] = useState(tableRowsData);

  // id 바인딩 함수
  const newIdBindFn = (arr: ICar[]) => arr.map((el: ICar) => ({ ...el, id: v4() }));

  const {
    data: carList,
    isLoading,
    isError,
  } = useQuery(['/schedule/car', userInfo.hCd], async () => {
    const res = await apiGet({ path: '/schedule/car', req: { cCd: userInfo?.hCd } });
    return res.data.data;
  });

  useEffect(() => {
    carList && setTableState(newIdBindFn(carList));
  }, [carList]);

  // 인풋 값 변경
  const onChangeInputValue = (e: ChangeEvent<HTMLInputElement>, idx: number) => {
    const { name, value } = e.target;
    const newValue = name === 'ccNum' ? characterRegex(value) : value;
    setTableState((prev: ICar[]) => prev.map((item: ICar, i: number) => (i === idx ? { ...item, [name]: newValue, flag: true } : item)));
  };

  // 체크유무 업데이트 함수
  const onClickUpdateYn = (val: 'Y' | 'N', idx: number) => {
    const newUseYn = val === 'Y' ? 'N' : 'Y';
    setTableState((prev: ICar[]) => prev.map((item: ICar, i: number) => (i === idx ? { ...item, useYn: newUseYn } : item)));
  };

  // Row 추가
  const onClickAddTableRow = () => {
    const newObj: ICar = {
      id: v4(),
      ccCd: '',
      ccName: '',
      ccNum: '',
      wDate: '',
      writer: '',
      eDate: '',
      editor: '',
      useYn: 'Y',
      flag: true,
    };
    setTableState((prev: ICar[]) => [...prev, newObj]);
  };

  // 저장
  const onClickSave = () => {
    setIsSaveClicked(true);
    // required 빈 값 체크
    const newRowRequired = ['ccName', 'ccNum'];

    const emptyCheck = tableState.filter((el: any) => {
      const check = newRowRequired.find((el2: any) => el[el2] === '');
      return check;
    });

    if (emptyCheck.length > 0) {
      toast.warning(t('필수입력값을 모두 입력하세요'));
    } else {
      saveCarAPI();
    }
  };

  // 저장 API
  const saveCarAPI = async () => {
    try {
      const req = {
        carSet: trimArray(
          tableState.map((item: ICar) => ({
            cCd: userInfo.hCd,
            ccCd: item.ccCd || '',
            ccName: item.ccName,
            ccNum: item.ccNum,
            useYn: item.useYn,
            flag: item.flag,
          }))
        ),
      };
      const res = await apiPost({ path: '/schedule/car', req });
      const { data: carListData, statusCode } = res.data;

      if (statusCode === 200) {
        setTableState(newIdBindFn(carListData));
        toast.success(t('저장되었습니다.'));
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '환경 설정 > 차량 설정',
          action: '저장',
          etc: `차량 설정 저장`,
        });
      }
    } catch (err) {
      console.error(err);
      throw new Error('error');
    }
  };

  // row 제거 및 삭제
  const onClickDelete = (el: ICar, idx: number) => {
    // 새로 추가한 로우 제거 클릭 시
    if (el.ccCd === '') {
      const newMap: ICar[] = [...tableState];
      newMap.splice(idx, 1);
      setTableState(newMap);
    } else {
      setOpenModal((prev) => ({ ...prev, status: true, state: tableState, setState: setTableState, api: deleteCarAPI, el, index: idx }));
    }
    // etIsTableInitialRender(true); // 삭제 클릭시 테이블길이 변화인한 스크롤하단 이동방지
  };

  // 삭제 API
  const deleteCarAPI = async (el: ICar) => {
    try {
      const req = {
        cCd: userInfo.hCd,
        ccCd: el.ccCd,
      };
      const res = await apiDelete({ path: '/schedule/car', req });
      const { data: carListData, statusCode } = res.data;
      if (statusCode === 200) {
        setTableState(newIdBindFn(carListData));
        toast.success(t('삭제되었습니다.'));
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '일정 관리 > 차량 관리',
          action: `차량 삭제`,
          etc: `차량 정보 : ${el.ccName} / ${el.ccNum}`,
        });
      }
    } catch (err) {
      console.error(err);
      throw new Error('error');
    }
  };

  return (
    <Root>
      <InputTable className='margin-left-05 margin-left-05 overflow-y-scroll'>
        <div className='thead'>
          <div className='tr'>
            {/* <div className='trCol2p5 flex-center tableStickyNo'>No</div> */}
            <div className='trCol4 flex-center'>{t('코드')}</div>
            <div className='trCol13 flex-center required'>{t('차량 명')}</div>
            <div className='trCol13 flex-center required'>{t('차량번호')}</div>
            <div className='trCol10 flex-center'>{t('사용유무')}</div>
            <div className='trCol13 flex-center content-overflow'>{t('등록일자')}</div>
            <div className='trCol10 flex-center'>{t('등록자')}</div>
            <div className='trCol13 flex-center content-overflow'>{t('수정일자')}</div>
            <div className='trCol10 flex-center'>{t('수정자')}</div>
            {auth.deleteAuth && <div className='trCol6 flex-center'> </div>}
          </div>
        </div>
        <div className='table' ref={scrollContainerRef}>
          <div className='tbody'>
            {tableState?.map((el: ICar, index: number) => (
              <div className='tr' role='button' tabIndex={0} key={el.id}>
                {/* <div className='trCol2p5 flex-center tableStickyNo'>{index + 1}</div> */}
                <div className='trCol4 flex-center'>{el.ccCd}</div>
                <div className='trCol13 flex-center'>
                  <input
                    type='text'
                    name='ccName'
                    className='colorButton'
                    value={el.ccName}
                    onChange={(e) => onChangeInputValue(e, index)}
                    style={isSaveClicked ? applyBorderStyle(el.ccName, 'red', 'ccName') : undefined}
                    onClick={(event) => {
                      if (size.innerSize.W <= 1024) {
                        event.stopPropagation();
                      }
                    }}
                    maxLength={25}
                  />
                </div>
                <div className='trCol13 flex-center'>
                  <input
                    type='text'
                    name='ccNum'
                    className='colorButton'
                    value={el.ccNum}
                    onChange={(e) => onChangeInputValue(e, index)}
                    style={isSaveClicked ? applyBorderStyle(el.ccNum, 'red', 'ccNum') : undefined}
                    onClick={(event) => {
                      if (size.innerSize.W <= 1024) {
                        event.stopPropagation();
                      }
                    }}
                    maxLength={10}
                  />
                </div>
                <div className='trCol10 flex-center'>
                  <ClickableContainer onClick={() => onClickUpdateYn(el.useYn, index)} button={<TableLamp use={el.useYn === 'Y'} />} />
                </div>
                <div className='trCol13 flex-center content-overflow scroll'>{el.wDate}</div>
                <div className='trCol10 flex-center'>{el.writer}</div>
                <div className='trCol13 flex-center content-overflow scroll'>{el.eDate}</div>
                <div className='trCol10 flex-center'>{el.editor}</div>
                {auth.deleteAuth && (
                  <div className='trCol6 flex-center'>
                    <BtnRed
                      onClick={(event) => {
                        if (size.innerSize.W <= 1024) {
                          event.stopPropagation();
                        }
                        onClickDelete(el, index);
                      }}
                    >
                      {el.ccCd !== '' ? t('삭제') : t('제거')}
                    </BtnRed>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </InputTable>
      {(auth.createAuth || auth.updateAuth) && (
        <ButtonsWrapper style={{ justifyContent: 'flex-end' }}>
          {auth.createAuth && <BtnBlue onClick={onClickAddTableRow}>{t('신규항목 추가')}</BtnBlue>}
          {(auth.createAuth || auth.updateAuth) && <BtnBlue onClick={onClickSave}>{t('저장')}</BtnBlue>}
        </ButtonsWrapper>
      )}
      <Portal openModal={openModal?.status}>{openModal && <DeleteModal openModal={openModal} setOpenModal={setOpenModal} />}</Portal>
    </Root>
  );
};

export default Vehicle;
